import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import * as api from "../../api/client";
import type { ApiCtx } from "../../api/client";
import type { LlmCall } from "../../api/types";
import { texte } from "../../texte";

/**
 * Laufende LLM-Aufrufe (in-flight): der Server registriert jeden Aufruf beim
 * Absenden (inkl. vollem Prompt) und löscht ihn mit der Antwort — dieser Tab
 * ist also ein Live-Fenster darauf, was die Agenten das Modell GERADE fragen.
 * Während eines Laufs wird im Poll-Takt aktualisiert.
 */
export function Llm({ ctx, running, pollSeconds }: { ctx: ApiCtx; running: boolean; pollSeconds: number }) {
  const query = useQuery({
    queryKey: ["llm", ctx.apiUrl, ctx.token, ctx.workspace],
    queryFn: () => api.llm(ctx),
    refetchInterval: running ? Math.max(2, pollSeconds) * 1000 : false,
  });
  const calls = query.data?.calls ?? [];
  const history = query.data?.history ?? [];

  const groupedHistory = useMemo(() => {
    const groups: { startedAt: string; agent: string; calls: LlmCall[] }[] = [];
    let currentGroup: { startedAt: string; agent: string; calls: LlmCall[] } | null = null;
    
    for (const call of history) {
      let group = groups.find(g => g.startedAt === call.started_at && g.agent === call.agent);
      if (!group) {
        group = { startedAt: call.started_at, agent: call.agent, calls: [] };
        groups.push(group);
      }
      group.calls.push(call);
    }
    
    // Sort loops DESC (newest first)
    groups.sort((a, b) => b.startedAt.localeCompare(a.startedAt));
    // Sort steps within loop DESC (newest first)
    for (const group of groups) {
      group.calls.sort((a, b) => (b.step ?? 0) - (a.step ?? 0));
    }
    return groups;
  }, [history]);

  if (!calls.length && !history.length) {
    return (
      <div className="empty" style={{ maxWidth: 560 }}>
        {running ? texte.llm.emptyRunning : texte.llm.emptyIdle}
      </div>
    );
  }

  return (
    <>
      <div className="log-tools">
        <span className="label">{texte.llm.title}</span>
        <span className="hint">{texte.llm.hint}</span>
      </div>
      {calls.length === 0 && (
        <div className="empty" style={{ maxWidth: 560, marginBottom: 20, marginTop: 10 }}>
          {running ? texte.llm.emptyRunning : texte.llm.emptyIdle}
        </div>
      )}
      {calls.map((call) => (
        <LlmCallCard key={call.call_id} call={call} />
      ))}
      
      {history.length > 0 && (
        <>
          <div className="log-tools" style={{ marginTop: 32, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <span className="label">{texte.llm.historyTitle}</span>
              <span className="hint">{texte.llm.historyHint}</span>
            </div>
            <button className="btn" onClick={() => downloadLlmHistory(history)}>{texte.llm.downloadHistory}</button>
          </div>
          {groupedHistory.map((group, gIdx) => (
            <div key={`${group.startedAt}-${group.agent}-${gIdx}`} style={{ marginBottom: 24 }}>
              <div style={{ marginBottom: 8, fontSize: 13, fontWeight: "bold", opacity: 0.8 }}>
                Loop started at: {group.startedAt}
              </div>
              {group.calls.map((call) => (
                <LlmCallCard key={call.call_id} call={call} isHistory />
              ))}
            </div>
          ))}
        </>
      )}
    </>
  );
}

function LlmCallCard({ call, isHistory }: { call: LlmCall; isHistory?: boolean }) {
  return (
    <div className="critic-block" style={{ opacity: isHistory ? 0.8 : 1 }}>
      <div className="ch">
        {call.step !== undefined && <span className="cid">Step {call.step}</span>}
        <span className="cid">{call.agent}</span>
        <span className="tgt">{call.model}</span>
        <span className="tgt">
          {texte.llm.template} {call.prompt_name}
        </span>
        <span className="tgt">
          {isHistory ? texte.llm.finishedAt : texte.llm.since} {isHistory ? call.finished_at : call.started_at}
        </span>
        {call.tokens !== undefined && (
          <span className="tgt">
            Tokens: {call.tokens}
          </span>
        )}
      </div>
      <details>
        <summary style={{ cursor: "pointer", fontSize: 13 }}>{texte.llm.instructions}</summary>
        <div className="logbox" style={{ whiteSpace: "pre-wrap", marginTop: 6 }}>
          {call.instructions || texte.llm.emptyField}
        </div>
      </details>
      <details open={!isHistory}>
        <summary style={{ cursor: "pointer", fontSize: 13 }}>{texte.llm.input}</summary>
        <div className="logbox" style={{ whiteSpace: "pre-wrap", marginTop: 6 }}>
          {call.input || texte.llm.emptyField}
        </div>
      </details>
      {call.tool_calls && call.tool_calls.length > 0 && (
        <details>
          <summary style={{ cursor: "pointer", fontSize: 13 }}>{texte.llm.toolCalls}</summary>
          {call.tool_calls.map((tc, idx) => {
            let content = tc.arguments;
            if (tc.name === "run_python") {
              try {
                const args = JSON.parse(tc.arguments);
                if (args.code) {
                  content = args.code;
                }
              } catch {
                // Ignore parse errors
              }
            }
            return (
              <div key={idx} style={{ marginTop: 8 }}>
                <strong style={{ fontSize: 13 }}>{tc.name}</strong>
                <div className="logbox" style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>
                  {content}
                </div>
              </div>
            );
          })}
        </details>
      )}
      {isHistory && (
        <details>
          <summary style={{ cursor: "pointer", fontSize: 13 }}>{texte.llm.result}</summary>
          <div className="logbox" style={{ whiteSpace: "pre-wrap", marginTop: 6 }}>
            {call.result || texte.llm.emptyField}
          </div>
        </details>
      )}
    </div>
  );
}

function downloadLlmHistory(history: LlmCall[]) {
  let md = "# LLM History\n\n";

  const groups: { startedAt: string; agent: string; calls: LlmCall[] }[] = [];
  for (const call of history) {
    let group = groups.find(g => g.startedAt === call.started_at && g.agent === call.agent);
    if (!group) {
      group = { startedAt: call.started_at, agent: call.agent, calls: [] };
      groups.push(group);
    }
    group.calls.push(call);
  }

  // Sort loops DESC (newest first)
  groups.sort((a, b) => b.startedAt.localeCompare(a.startedAt));
  // Sort steps within loop DESC (newest first)
  for (const group of groups) {
    group.calls.sort((a, b) => (b.step ?? 0) - (a.step ?? 0));
  }

  for (let i = 0; i < groups.length; i++) {
    const group = groups[i];
    md += `## Loop #${i + 1}: ${group.agent}\n`;
    md += `- **Started At:** ${group.startedAt}\n\n`;
    for (const call of group.calls) {
      md += `### Step ${call.step ?? "?"} (${call.model})\n`;
      md += `- **Template:** ${call.prompt_name}\n`;
      md += `- **Finished At:** ${call.finished_at || ""}\n`;
      if (call.tokens !== undefined) {
        md += `- **Tokens:** ${call.tokens}\n`;
      }
      md += `\n#### Instructions\n${call.instructions}\n\n`;
      if (call.input) {
        md += `#### Input\n${call.input}\n\n`;
      }
      
      if (call.tool_calls && call.tool_calls.length > 0) {
        md += `#### Tool Calls\n`;
        for (const tc of call.tool_calls) {
          md += `##### ${tc.name}\n`;
          let content = tc.arguments;
          if (tc.name === "run_python") {
            try {
              const args = JSON.parse(tc.arguments);
              if (args.code) {
                content = args.code;
              }
            } catch {
              // Ignore parse errors
            }
            md += "```python\n" + content + "\n```\n\n";
          } else {
            md += "```json\n" + content + "\n```\n\n";
          }
        }
      }
      if (call.result) {
        md += `#### Result\n${call.result}\n\n`;
      }
      md += `---\n\n`;
    }
  }

  const blob = new Blob([md], { type: "text/markdown" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `llm_history_${new Date().toISOString().replace(/[:.]/g, "-")}.md`;
  a.click();
  URL.revokeObjectURL(url);
}
